import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { ColDef, RowClassParams, RowStyle } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'

import { AgGridRef, useRegisterAgGridRefEffect } from '@contexts/grid-ref-context'
import { formatNumber } from '@utils/number-utils'
import { FAILURE_ROW_COLOR } from '@utils/style-utils'

import Button from '@core/button'
import { Separator } from '@core/separator'

import AgGrid from '@components/ag-grid'
import { PythonCellRenderer } from '@components/ag-grid/custom-cell-renderer/python-cell-renderer'
import StatusBar from '@components/ag-grid/status-bar'
import AgGridSearchInput from '@components/control-panel/ag-grid-search-input'

import { useFetchStreamStatusDetails } from '@layout-components/general/data-connections/queries/fetch-stream-status-details'
import JobDetail from '@layout-components/general/data-connections/shared/data-movement-jobs/job-detail'
import PendingStreams, {
  PendingStreamRow
} from '@layout-components/general/data-connections/shared/data-movement-jobs/pending-streams'
import {
  durationColumnDef,
  multiTextColumnDef,
  numericCommaSeparatedColumnDef,
  timeZoneDateColumnDef
} from '@layout-components/general/data-connections/utils/col-defs'

import { selectUserBusinessTimeZone } from '@store/slices/user-preferences'

import { useFetchJobHistory } from './queries/fetch-job-history'
import { SyncStatusCellRenderer } from './sync-status-cell-renderer'
import { IConnectedSourceSystem, JobData, LAST_SYNC_STATUS } from './types'

const STREAM_STATUS_COMPLETED = 'complete'
export interface StreamState {
  tableName: string
  status: string
}

const ClickableCellRenderer = (params: any) => {
  return (
    <div className='cursor-pointer text-blue underline hover:text-primary-darker'>
      {params.value}
    </div>
  )
}

const transformPendingStreamsData = (streamStates: StreamState[]): PendingStreamRow[] => {
  return _.chain(streamStates)
    .toPairs()
    .filter(([_, details]) => details.status !== STREAM_STATUS_COMPLETED)
    .map(([streamName]) => ({ streamName }))
    .value()
}

export function JobHistory({
  connectedSourceSystem,
  isPending
}: {
  connectedSourceSystem?: IConnectedSourceSystem
  isPending: boolean
}) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<AgGridReact>(null)
  const [groupByStreamStatus, setGroupByStreamStatus] = useState(false)
  const [selectedStreamStatusId, setSelectedStreamStatusId] = useState<string | undefined | null>(
    null
  )

  const [streamStatusData, setStreamStatusData] = useState<JobData | null>(null)
  const { data: streamStatusDetails } = useFetchStreamStatusDetails({
    businessId: connectedSourceSystem?.businessId,
    connectedSourceSystemId: connectedSourceSystem?.id,
    streamStatusId: selectedStreamStatusId ?? undefined
  })

  const streamStates = useMemo(() => {
    return streamStatusDetails?.streamStatusesStreamStates || '{}'
  }, [streamStatusDetails])

  const timeZone = useSelector(selectUserBusinessTimeZone)

  const { isPending: isPendingJobs, data } = useFetchJobHistory({
    id: connectedSourceSystem?.id,
    businessId: connectedSourceSystem?.businessId
  })

  useRegisterAgGridRefEffect(gridRef as AgGridRef)

  const streamStatesObj = useMemo(() => {
    return JSON.parse(streamStates)
  }, [streamStates])

  const hasStreamStates = useMemo(() => !_.isEmpty(streamStatesObj), [streamStatesObj])

  const pendingStreamsData = useMemo(
    () => (hasStreamStates ? transformPendingStreamsData(streamStatesObj) : []),
    [hasStreamStates, streamStatesObj]
  )
  const hasPendingStreams = pendingStreamsData.length > 0

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'jobId',
        headerName: 'Job ID',
        initialWidth: 100,
        maxWidth: 350
      },
      multiTextColumnDef('streamStatusesId', 'Stream Status ID', {
        initialWidth: 80,
        maxWidth: 140,
        rowGroup: groupByStreamStatus,
        cellRenderer: ClickableCellRenderer,
        hide: groupByStreamStatus
      }),
      timeZoneDateColumnDef('startTime', 'Start Time', timeZone, {
        initialWidth: 150
      }),
      timeZoneDateColumnDef('endTime', 'End Time', timeZone, {
        initialWidth: 150
      }),
      durationColumnDef('duration', 'Duration', 'startTime', 'endTime', true, {
        initialWidth: 100
      }),
      numericCommaSeparatedColumnDef('numOfRecordsRead', 'Records Read', {
        valueFormatter: (params) => (params.value ? formatNumber(params.value) : '')
      }),
      multiTextColumnDef('status', 'Status', {
        cellRenderer: SyncStatusCellRenderer,
        maxWidth: 80,
        valueGetter: (params) => {
          if (params.data && params.data.status) {
            return LAST_SYNC_STATUS[params.data.status] || null
          }
          return null
        },
        initialWidth: 80
      }),
      {
        field: 'failureReason',
        headerName: 'Reason',
        cellRenderer: PythonCellRenderer,
        cellRendererParams: {
          title: 'Reason'
        },
        initialWidth: 100,
        maxWidth: 400
      }
    ],
    [groupByStreamStatus, timeZone]
  )

  const isLoading = isPending || isPendingJobs

  const getRowStyle = (params: RowClassParams<any, any>): RowStyle | undefined => {
    if (params.data?.status === 'failed') {
      return { backgroundColor: FAILURE_ROW_COLOR }
    }
    return undefined
  }

  const handleGroupByStreamStatus = () => {
    const gridApi = gridRef.current?.api
    if (!gridApi) return

    const currentColumnState = gridApi.getColumnState()

    setGroupByStreamStatus((prevState) => {
      const newGroupByStreamStatus = !prevState

      const newState = currentColumnState.map((state) => {
        if (state.colId === 'streamStatusesId') {
          return {
            ...state,
            rowGroup: newGroupByStreamStatus,
            hide: !newGroupByStreamStatus
          }
        }
        return state
      })

      gridApi.applyColumnState({ state: newState })

      if (newGroupByStreamStatus) {
        gridApi.expandAll()
      }

      gridApi.onGroupExpandedOrCollapsed()

      return newGroupByStreamStatus
    })
  }

  useEffect(() => {
    const gridApi = gridRef.current?.api
    if (gridApi) {
      gridApi.expandAll()
    }
  }, [data])

  const getGridWidths = () => {
    if (!streamStatusData) {
      return {
        firstGridWidth: '100%',
        secondGridWidth: '0%',
        thirdGridWidth: '0%'
      }
    }

    if (hasStreamStates && hasPendingStreams) {
      return {
        firstGridWidth: '60%',
        secondGridWidth: '30%',
        thirdGridWidth: '10%'
      }
    } else if (hasStreamStates && !hasPendingStreams) {
      return {
        firstGridWidth: '60%',
        secondGridWidth: '40%',
        thirdGridWidth: null
      }
    } else if (!hasStreamStates && hasPendingStreams) {
      return {
        firstGridWidth: '60%',
        secondGridWidth: null,
        thirdGridWidth: '15%'
      }
    } else {
      return {
        firstGridWidth: '100%',
        secondGridWidth: null,
        thirdGridWidth: null
      }
    }
  }

  const { firstGridWidth, secondGridWidth, thirdGridWidth } = getGridWidths()

  return (
    <div className='h-[calc(100vh-16rem)] w-full' ref={wrapperRef}>
      <div className='mb-2 flex min-h-11 w-full items-center space-x-2'>
        <AgGridSearchInput />
        <Separator orientation='vertical' className='h-10 text-grey-lighter' />
        <Button variant='outline' onClick={handleGroupByStreamStatus} className='h-7'>
          {groupByStreamStatus ? 'Show All Jobs' : 'Organize by Parallel Jobs'}
        </Button>
      </div>
      <Separator className='w-full' />

      <div className='mt-4 flex' style={{ height: '100%', width: '100%' }}>
        <AgGrid
          style={{ height: '100%', width: firstGridWidth }}
          ref={gridRef}
          rowData={data}
          columnDefs={columnDefs}
          defaultColDef={{
            minWidth: 50,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            filterParams: {
              buttons: ['clear']
            }
          }}
          autoGroupColumnDef={multiTextColumnDef('streamStatusesId', 'Stream', {
            maxWidth: 150,
            initialWidth: 80,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
              innerRenderer: (params: any) => {
                if (params.node.group) {
                  return <ClickableCellRenderer {...params} />
                }
                return ''
              }
            }
          })}
          autoSizeStrategy={{ type: 'fitCellContents' }}
          loading={isLoading}
          getRowStyle={getRowStyle}
          groupDisplayType='singleColumn'
          groupRemoveSingleChildren={false}
          groupRemoveLowestSingleChildren={false}
          groupDefaultExpanded={-1}
          suppressAggFuncInHeader={true}
          onCellClicked={(params) => {
            if (params.colDef.field === 'streamStatusesId') {
              if (params.data) {
                setStreamStatusData(params.data as JobData)
                setSelectedStreamStatusId(params.data.streamStatusesId)
              } else if (params.node?.allLeafChildren && params.node.allLeafChildren.length > 0) {
                const job = params.node.allLeafChildren[0].data as JobData
                setStreamStatusData(job)
                setSelectedStreamStatusId(job?.streamStatusesId)
              } else {
                setStreamStatusData(null)
              }
            }
          }}
          statusBar={{
            statusPanels: [
              {
                statusPanel: StatusBar,
                statusPanelParams: { aggFuncs: ['sum', 'avg', 'count', 'totalDuration'] }
              }
            ]
          }}
        />

        {streamStatusData && hasStreamStates && secondGridWidth && (
          <JobDetail
            timeZone={timeZone}
            streamStatusesId={streamStatusData.streamStatusesId}
            gridWidth={secondGridWidth}
            streamStatusesStreamStates={streamStates}
          />
        )}

        {streamStatusData && pendingStreamsData && thirdGridWidth && (
          <PendingStreams
            streamStatusesId={streamStatusData.streamStatusesId}
            gridWidth={thirdGridWidth}
            pendingStreamsData={pendingStreamsData}
          />
        )}
      </div>
    </div>
  )
}
